import { Link } from 'react-router-dom';
import { BlogPost } from '@/lib/supabase';
import { Calendar, Clock, ArrowRight } from 'lucide-react';

interface StandardPostProps {
  post: BlogPost;
}

export function StandardPost({ post }: StandardPostProps) {
  const readTime = Math.ceil((post.content?.split(/\s+/).length || 0) / 200);

  return (
    <Link 
      to={`/blog/${post.slug}`}
      className="group flex flex-col md:flex-row gap-6 bg-white p-4 rounded-2xl border border-transparent hover:border-slate-200 hover:shadow-lg transition-all duration-300"
    >
      {/* Imagen (Izquierda en desktop) */}
      <div className="w-full md:w-1/3 aspect-[16/9] md:aspect-[4/3] rounded-xl overflow-hidden bg-slate-100 shrink-0 relative">
        {post.image_url ? (
          <img 
            src={post.image_url} 
            alt={post.title} 
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-slate-200 text-slate-400 font-medium">
            Sin imagen
          </div>
        )}
        {post.categories && (
          <div className="absolute top-2 left-2 md:hidden bg-white/90 backdrop-blur px-2 py-1 rounded text-xs font-bold text-blue-700 uppercase">
            {post.categories.name}
          </div>
        )}
      </div>

      {/* Contenido (Derecha) */}
      <div className="flex-1 flex flex-col justify-center">
        <div className="flex items-center gap-3 text-xs font-bold uppercase tracking-wide mb-2">
          {post.categories && (
            <span className="text-blue-600 hidden md:inline-block">
              {post.categories.name}
            </span>
          )}
          <span className="text-slate-400 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {new Date(post.published_at || post.created_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}
          </span>
        </div>

        <h3 className="text-xl font-bold text-slate-900 mb-3 leading-snug group-hover:text-blue-600 transition-colors">
          {post.title}
        </h3>

        <p className="text-slate-600 text-sm line-clamp-2 mb-4 leading-relaxed">
          {post.excerpt}
        </p>

        <div className="mt-auto flex items-center justify-between border-t border-slate-100 pt-3">
          <div className="flex items-center gap-1 text-xs text-slate-500 font-medium">
            <Clock className="w-3.5 h-3.5" />
            {readTime} min lectura
          </div>
          <span className="text-sm font-bold text-blue-600 flex items-center gap-1 group-hover:translate-x-1 transition-transform">
            Leer más <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
}
